/**
 * Main JS file for project.
 */

 /* global Ractive, geoip2, moment */
'use strict';

// Dependencies
import utils from './utils.js';
import statesLookup from './states.js';
import tap from 'ractive-events-tap/dist/ractive-events-tap.es.js';
import request from 'superagent';
import _ from 'underscore';
import smoothscroll from 'smoothscroll-polyfill';
import queryString from 'query-string';

// Setup
const u = utils({ pym: true });
Ractive.events.tap = tap;
smoothscroll.polyfill();
const cacheExpirationMS = 1000 * 60 * 60 * 24;

// Determine initial view
const viewChangeEl = document.getElementsByClassName('national-totals')[0];
let defaultView = viewChangeEl.classList.contains('show-vote') ? 'vote' : 'position';

// Main template
const r = new Ractive({
  el: '.filtering',
  template: document.getElementById('filter-template').innerHTML,
  partials: {
    group: document.getElementById('group-template').innerHTML,
    congressperson: document.getElementById('congressperson-template').innerHTML
  },
  data: {
    view: defaultView,
    hasSearch: false,
    hasResults: false,
    states: Object.keys(statesLookup),
    statesLookup: statesLookup,
    dateFormatAP: u.dateFormatAP,
    timeFormatAP: u.timeFormatAP,
    voteTranslation: {
      'Yes': 'Yes',
      'No': 'No',
      'Not Voting': 'Not voting',
      'Not Present': 'Not present for vote'
    }
  }
});

// View switcher template
const switcher = new Ractive({
  el: '.view-switcher',
  template: document.getElementById('view-switcher-template').innerHTML,
  data: {
    view: defaultView
  }
});

// Change view on view switcher
switcher.observe('view', (v) => {
  r.set('view', v);
});

switcher.on('switch', (e, v) => {
  if (e && e.original) {
    e.original.preventDefault();
  }
  switcher.set('view', v);
});


// View change
r.observe('view', (v) => {
  viewChangeEl.classList.remove(v === 'vote' ? 'show-position' : 'show-vote');
  viewChangeEl.classList.add(v === 'vote' ? 'show-vote' : 'show-position');
  r.update('results');
});

// State change
r.observe('searchState', (state) => {
  if (state && state !== 'choose') {
    getState(state);
  }
});

// When results updated, split apart into groups
r.observe('results', (n) => {
  n = n || [];

  if (r.get('view') === 'vote') {
    r.set('groups', [
      { id: 'yes', name: 'Yes', results: _.filter(n, { vote: 'Yes' }) },
      { id: 'no', name: 'No', results: _.filter(n, { vote: 'No' }) },
      { id: 'not-voting', name: 'Not voting', results: _.filter(n, { vote: 'Not Voting' }) },
      { id: 'not-present', name: 'Not present', results: _.filter(n, { vote: 'Not Present' }) },
    ]);
  }
  else {
    r.set('groups', [
      { id: 'favor', name: 'In favor', results: _.filter(n, { position: 'In favor' }) },
      { id: 'opposed', name: 'Opposed', results: _.filter(n, { position: 'Opposed' }) },
      { id: 'equivocal', name: 'Equivocal', results: _.filter(n, { position: 'Equivocal' }) },
      { id: 'no-statement', name: 'No statement', results: _.filter(n, (d) => {
        return !d.position || d.position === 'No statement' || d.position === 'Not collected';
      }) },
    ]);
  }
});

// Handle submit.  We can just ignore it
r.on('submitSearch', (e) => {
  if (e && e.original) {
    e.original.preventDefault();
  }
});

// Scroll to id
r.on('goTo', (e, id) => {
  if (e && e.original) {
    e.original.preventDefault();
  }
  const el = document.getElementById(id);
  if (!el) {
    return;
  }

  if (u.isEmbedded() && u.pym) {
    u.pym.scrollParentToChildEl(id);
  }
  else {
    el.scrollIntoView({ behavior: 'smooth' });
  }
});

// Non template events
document.getElementsByClassName('embed-link')[0].addEventListener('click', (e) => {
  e.preventDefault();
  document.getElementsByClassName('embed')[0].classList.toggle('active');
});

// Add body class if embed
if (u.isEmbedded()) {
  document.body.className = document.body.className ? document.body.className += ' embedded' : 'embedded';
}

// Read state from query paramter, getState will check for valid state
const query = queryString.parse(document.location.search);
if (query.state) {
  getState(query.state);
}
else {
  try {
    geolocate(getState);
  }
  catch (e) {
    getState();
  }
}


// Get data for specific state and
function getState(state) {
  state = state ? state.toUpperCase() : '';

  if (~Object.keys(statesLookup).indexOf(state)) {
    request.get('./assets/data/states/' + state.toUpperCase() + '.json', (error, response) => {
      if (error) {
        // TODO: Handle error
        return console.error(error);
      }

      // Parse, translare
      let data = response.body.map((d) => {
        d.quoteDateM = d.quote_date ? moment(d.quote_date, 'MM/DD/YYYY') : undefined;
        d.timestampM = d.timestampUnix ? moment.unix(d.timestampUnix) : undefined;
        return d;
      });

      // Update interface
      r.set('results', data);
      r.set('searchState', state);
      r.set('hasResults', true);
      r.set('hasSearch', true);

      if (u.hasLocalStorage()) {
        localStorage.setItem('congress-tracker-status-location', JSON.stringify({
          'expiration': cacheExpirationMS,
          'time': new Date().getTime(),
          'statePostal': state
        }));
      }
    });
  }
  else {
    r.set('hasResults', false);
    r.set('hasSearch', true);
  }
}

// Geolocate
function geolocate(done) {
  // Try cache
  var lookup;
  try {
    lookup = JSON.parse(localStorage.getItem('congress-tracker-status-location'));
  }
  catch(e) {
    // console.error(e);
  }

  if (!_.isObject(lookup) || (new Date().getTime()) - lookup.time > lookup.expiration) {
    geoip2.city(onLocateSuccess, onLocateFail);
  }
  else {
    done(lookup.statePostal);
  }

  // Geolocate success callback
  function onLocateSuccess(resp) {
    const statePostal = resp.most_specific_subdivision.iso_code;

    if (u.hasLocalStorage()) {
      localStorage.setItem('congress-tracker-status-location', JSON.stringify({
        'expiration': cacheExpirationMS,
        'time': new Date().getTime(),
        'statePostal': statePostal
      }));
    }

    done(statePostal);
  }

  function onLocateFail(error) {
    console.error(error);
    done(false);
  }
}



// WEBPACK FOOTER //
// ./app/index.js